import React, { useEffect, useState } from "react";
import { CalendarPlus, Apple, Copy, Check, Download, Settings, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import CalendarImport from "@/components/CalendarImport";

function Row({ icon: Icon, title, desc, onClick, testId }) {
  return (
    <div onClick={onClick} data-testid={testId}
      className="flex w-full cursor-pointer items-start gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-slate-100">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100">
        <Icon className="h-4 w-4 text-slate-700" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </div>
  );
}

export default function CalendarMenu({ onChange, canImport = false, label = "Settings" }) {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    api.get("/schedule/ical-token")
      .then(({ data }) => setUrl(`${process.env.REACT_APP_BACKEND_URL || ""}${data.path}`))
      .catch(() => {});
  }, []);

  const webcalUrl = url.replace(/^https?:\/\//, "webcal://");
  const googleSub = url ? `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcalUrl)}` : "#";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Calendar link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="rounded-full" data-testid="calendar-menu-btn">
            <Settings className="mr-2 h-4 w-4" /> {label} <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2" data-testid="calendar-menu-content">
          <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-testid="calendar-menu-link-heading">
            Link RallyPointGo events to another calendar
          </p>
          <a href={googleSub} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid="subscribe-google-btn">
            <Row icon={CalendarPlus} title="Subscribe in Google" desc="Show RallyPointGo events in your Google Calendar" />
          </a>
          <a href={webcalUrl} onClick={() => setOpen(false)} data-testid="subscribe-apple-btn">
            <Row icon={Apple} title="Subscribe in Apple Calendar" desc="Show RallyPointGo events in Apple Calendar (iPhone / Mac)" />
          </a>
          <Row icon={copied ? Check : Copy} title="Copy calendar link"
            desc="Copy the feed link to paste into any calendar app" onClick={copy} testId="copy-ical-link-btn" />

          {canImport && (
            <>
              <div className="my-1 border-t border-slate-100" />
              <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Bring events in
              </p>
              <Row icon={Download} title="Import a calendar"
                desc="Pull events from a Google / Apple calendar into RallyPointGo"
                onClick={() => { setOpen(false); setImportOpen(true); }} testId="calendar-import-open-btn" />
            </>
          )}
        </PopoverContent>
      </Popover>
      {canImport && <CalendarImport onChange={onChange} open={importOpen} onOpenChange={setImportOpen} hideTrigger />}
    </>
  );
}
